import React, {useEffect, useState} from "react";
import {Button, Card, Checkbox, Col, Form, Input, Row, Select, Space} from "antd";
import {AgentNewContext} from "../../agent.new.modal";
import {useRequiredStore} from "../../../../../../utils/store";
import {RootStoreContext} from "../../../../../../stores/root/root.store";
import {useTranslate} from "../../../../../../hooks/translate.hook";
import {observer} from "mobx-react";
import {UIInputWidgetAccountBank} from "../../../../ui/input/account-bank/ui.input.widget.account-bank";
import {useAgentNewStepCardStyles} from "../agent.new.step.card.styles";

export const GeneralStep: React.FC = observer(() => {

    const {setStep, setValue, getValue, fields} = React.useContext(AgentNewContext);

    const { langStore, commonStore } = useRequiredStore(RootStoreContext);

    const t = useTranslate();

    const [loading, setLoading] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(false);

    const [form] = Form.useForm();

    useEffect(() => {
        form.setFields(fields);
    }, [fields])

    const defaultValues = {
        account: getValue('account'),
        agent: getValue('agent')
    }
    const cardStyles = useAgentNewStepCardStyles();

    return (
        <Form
            size={"middle"}
            form={form}
            layout="vertical"
            name={"client_form"}
            initialValues={defaultValues}
            onFinish={async (values) => {
                await form.validateFields();
                setValue('account', values.account)
                setValue('agent', values.agent)
                setStep(1)
            }}
        >
            <Space direction="vertical" size={16} style={{width: "100%"}}>
                <Row gutter={[16, 16]} align="top" style={{width: "100%"}}>
                    <Col xs={24} md={12}>
                        <div style={{display: "flex", flexDirection: "column", gap: 16}}>
                            <Card
                                size="small"
                                title={t("CLIENT.AGENT_DETAILS", {defaultValue: "Agent details"})}
                                style={{width: "100%"}}
                                styles={cardStyles}
                            >
                                <Form.Item
                                    name={["agent", "businessName"]}
                                    label={t("BUSINESS_NAME")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <Input disabled={loading || disabled} type="text" placeholder={t("USE_LEGAL_NAME")} />
                                </Form.Item>
                                <Form.Item
                                    name={["agent", "name"]}
                                    label={t("CLIENT.NAME")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <Input disabled={loading || disabled} type="text" placeholder={t("USE_LEGAL_NAME")} />
                                </Form.Item>
                                <Form.Item
                                    name={["agent", "group"]}
                                    label={t("CLIENT.GROUP")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <Select
                                        disabled={loading || disabled}
                                        placeholder={t("CLIENT.GROUP")}
                                    >
                                        {commonStore.agentGroups.map((item) => (
                                            <Select.Option key={item} value={item}>
                                                {item}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    name={["agent", "country"]}
                                    label={t("COUNTRY")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder={t("COUNTRY_SELECT")}
                                        showSearch
                                        optionFilterProp={"children"}
                                    >
                                        {langStore.countries.map(({ id, name }) => (
                                            <Select.Option key={id as string} value={id as string}>
                                                {name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item name={["agent", "comment"]} label={t("COMMENT")}>
                                    <Input.TextArea
                                        disabled={loading || disabled}
                                        autoSize={{minRows: 2, maxRows: 4}}
                                    />
                                </Form.Item>
                            </Card>

                            <Card
                                size="small"
                                title={t("CLIENT.IC.TITLE")}
                                style={{width: "100%"}}
                                styles={cardStyles}
                            >
                                <Form.Item
                                    name={["agent", "hasNumber"]}
                                    valuePropName="checked"
                                >
                                    <Checkbox disabled={loading || disabled}>
                                        {t("AGENT.AGENT_HAS_NUMBER")}
                                    </Checkbox>
                                </Form.Item>
                                <Form.Item noStyle shouldUpdate>
                                    {({getFieldValue}) =>
                                        getFieldValue(["agent", "hasNumber"]) ? (
                                            <Form.Item
                                                name={["agent", "number"]}
                                                label={t("NUMBER")}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                                    },
                                                ]}
                                            >
                                                <Input disabled={loading || disabled} />
                                            </Form.Item>
                                        ) : null
                                    }
                                </Form.Item>
                                <Form.Item
                                    name={["agent", "icAccessGroup"]}
                                    label={t("CLIENT.IC.ACCESS_GROUP")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                        },
                                    ]}
                                >
                                    <Select disabled={loading || disabled} placeholder={t("CLIENT.IC.ACCESS_GROUP")}>
                                        {commonStore.agentICAccessGroups.map((item) => (
                                            <Select.Option key={item.id} value={item.id}>
                                                {item.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    name={["agent", "groupNumber"]}
                                    label={t("CLIENT.PARTNER_CODE")}
                                >
                                    <Input disabled={loading || disabled} type="text" placeholder={t("CLIENT.PARTNER_CODE")} />
                                </Form.Item>
                            </Card>
                        </div>
                    </Col>

                    <Col xs={24} md={12}>
                        <Card
                            size="small"
                            title={t("CLIENT.ACCOUNT_DETAILS", {defaultValue: "Account details"})}
                            style={{width: "100%"}}
                            styles={cardStyles}
                        >
                            <Form.Item
                                name={["account", "legalType"]}
                                label={t("CLIENT.TYPE")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("FROM.ERROR.PLEASE_SELECT_VALUE"),
                                    },
                                ]}
                            >
                                <Select
                                    disabled={loading || disabled}
                                    placeholder={t("CLIENT.TYPE")}
                                    showSearch
                                    allowClear
                                    optionFilterProp={"children"}
                                >
                                    {commonStore.agentTypes.map((item) => (
                                        <Select.Option key={item} value={item}>
                                            {item.beautify()}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name={["account", "language"]}
                                label={t("LOCALES")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("FROM.ERROR.PLEASE_SELECT_VALUE"),
                                    },
                                ]}
                            >
                                <Select
                                    placeholder={t("LOCALES")}
                                    disabled={loading || disabled}
                                >
                                    <Select.Option value={"en"}>EN</Select.Option>
                                    <Select.Option value={"bg"}>BG</Select.Option>
                                    <Select.Option value={"sr"}>SR</Select.Option>
                                    <Select.Option value={"de"}>DE</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name={["account", "bank"]}
                                label={t("BANK.NAME")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                    },
                                ]}
                            >
                                <UIInputWidgetAccountBank disabled={loading || disabled} />
                            </Form.Item>
                            <Form.Item
                                name={["account", "orderType"]}
                                label={t("CLIENT.ORDER_TYPE.TITLE")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                    },
                                ]}
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    optionFilterProp={"children"}
                                    disabled={loading || disabled}
                                    placeholder={t("CLIENT.ORDER_TYPE.TITLE")}
                                >
                                    {commonStore.orderTypes.map((item) => (
                                        <Select.Option value={item} key={item}>
                                            {t("CLIENT.ORDER_TYPE." + item.toUpperCase())}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>

                <Row justify="end">
                    <Col>
                        <Button type="primary" onClick={() => form.submit()}>
                            {t("NEXT", {defaultValue: "Next"})}
                        </Button>
                    </Col>
                </Row>
            </Space>
        </Form>
    );
})
